import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { CheckCircle, XCircle, Loader2, Star, Heart, Sparkles, ExternalLink, Clock, AlertTriangle, Lock } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────
// 3+1 split: Q1-Q3 are answered on day 0 (same-day stage). Q4 — "did the
// plan work?" — is locked until day 6 has passed so the patient has
// actually lived through the treatment. We never send SMS for Q4; the
// patient receives a free web-push notification on their installed PWA.
// ─────────────────────────────────────────────────────────────────────────

const SAMEDAY_QUESTIONS = [
  {
    key: 'q1', weight: 25,
    title: 'Did the doctor hear your symptoms and condition fully?',
    sub: 'You felt the doctor listened to everything you wanted to share — without rushing or interrupting.',
  },
  {
    key: 'q2', weight: 25,
    title: 'Did the doctor ask follow-up questions, examine the affected parts, explain what was happening, and address your concerns?',
    sub: 'Clinical examination + plain-language explanation + actively answering the worries you had.',
  },
  {
    key: 'q3', weight: 20,
    title: 'Did the doctor tell you the next steps, the reason for them, and the likely success rate of healing?',
    sub: 'A clear plan with the "why" behind it and an honest expectation of outcome.',
  },
];

const OUTCOME_QUESTION = {
  key: 'q4', weight: 30,
  title: 'After about 1 week of treatment, did the plan work fairly correctly as the doctor had explained?',
  sub: 'Honest reflection on whether you felt better the way the doctor predicted.',
};

export default function TqsSurveyPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [answers, setAnswers] = useState({});
  const [q4, setQ4] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = async () => {
    try {
      const [survey, state] = await Promise.all([
        api.get(`/tqs/survey/${token}`).then(r => r.data).catch(() => null),
        api.get(`/tqs/survey/${token}/state`).then(r => r.data).catch(() => null),
      ]);
      if (survey?.status === 'completed') {
        setResult({ score: survey.score, doctor_name: survey.doctor_name, gbp_review_url: survey.gbp_review_url, alreadyDone: true });
      } else if (state) {
        setMeta({
          doctor_name: state.doctor_name,
          doctor_slug: state.doctor_slug,
          appointment_at: state.appointment_at,
          sameday_completed: state.sameday_completed,
          outcome_completed: state.outcome_completed,
          outcome_unlocked: state.outcome_unlocked,
          outcome_unlocks_at: state.outcome_unlocks_at,
        });
      } else if (survey) {
        setMeta({ doctor_name: survey.doctor_name, doctor_slug: survey.doctor_slug, appointment_at: survey.appointment_date });
      } else {
        setErr('Survey link not found');
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Survey link not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  // ── What stage are we showing? ───────────────────────────────────────
  // sameday-pending → ask Q1-Q3
  // outcome-pending  → ask Q4 (only when day-6+ unlocked)
  // both done        → thank-you
  const samedayDone = !!meta?.sameday_completed;
  const outcomeDone = !!meta?.outcome_completed;
  const outcomeUnlocked = !!meta?.outcome_unlocked;

  const samedayAnswered = SAMEDAY_QUESTIONS.every(q => typeof answers[q.key] === 'boolean');
  const q4Answered = typeof q4 === 'boolean';

  const submitSameday = async () => {
    if (!samedayAnswered) return;
    setSubmitting(true); setErr('');
    try {
      await api.post(`/tqs/survey/${token}/sameday`, { q1: answers.q1, q2: answers.q2, q3: answers.q3 });
      await refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  const submitOutcome = async () => {
    if (!q4Answered) return;
    setSubmitting(true); setErr('');
    try {
      const { data } = await api.post(`/tqs/survey/${token}/outcome`, { q4, note });
      // After outcome submission, fetch the completed survey for score
      const { data: survey } = await api.get(`/tqs/survey/${token}`);
      setResult({ score: survey.score, doctor_name: survey.doctor_name, gbp_review_url: survey.gbp_review_url, freshness: data?.freshness });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-sky-600" size={28} />
    </div>
  );

  if (err && !meta && !result) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center">
        <XCircle size={32} className="text-rose-500 mx-auto mb-3" />
        <p className="text-base font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{err}</p>
        <p className="text-xs text-slate-500 mt-2">If your doctor sent you this link, please ask them to send a new one.</p>
      </div>
    </div>
  );

  if (result) {
    const score = result.score;
    const star = result.star_rating ?? (score != null ? Math.round((score / 100) * 5 * 2) / 2 : 0);
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-sky-50 to-violet-50 flex items-center justify-center p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="bg-white rounded-3xl border border-slate-200 p-8 max-w-lg w-full shadow-xl" data-testid="tqs-thankyou">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Heart size={28} className="text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {result.alreadyDone ? 'Thanks again' : 'Thank you'}
            </h1>
            <p className="text-sm text-slate-600 mt-1">Your honest answers help <b>{result.doctor_name}</b> raise the bar.</p>
          </div>

          {!result.alreadyDone && score != null && (
            <div className="mt-6 bg-slate-50 rounded-2xl p-5 text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Treatment Quality Score</p>
              <p className="text-5xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="tqs-score">{score}<span className="text-2xl text-slate-400">/100</span></p>
              <div className="flex justify-center gap-1 mt-2">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={20} className={i <= Math.floor(star) ? 'text-amber-400 fill-amber-400' : i - 0.5 <= star ? 'text-amber-400 fill-amber-400/50' : 'text-slate-200'} />
                ))}
              </div>
            </div>
          )}

          {result.gbp_review_url && (
            <div className="mt-6 bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={16} className="text-sky-600" />
                <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>One more favour?</p>
              </div>
              <p className="text-xs text-slate-600 mb-3">Help future patients find {result.doctor_name} on Google Maps. It takes 30 seconds.</p>
              <a href={result.gbp_review_url} target="_blank" rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                data-testid="tqs-gbp-cta">
                Leave a Google review <ExternalLink size={14} />
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Sameday completed but outcome stage locked (waiting for day 6) ────
  if (samedayDone && !outcomeDone && !outcomeUnlocked) {
    const unlockAt = meta?.outcome_unlocks_at ? new Date(meta.outcome_unlocks_at) : null;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="bg-white rounded-3xl border border-slate-200 p-8 max-w-lg w-full shadow-sm text-center" data-testid="tqs-outcome-locked">
          <div className="w-14 h-14 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center mx-auto mb-3">
            <Lock size={22} className="text-sky-600" />
          </div>
          <p className="text-xs font-bold text-sky-600 uppercase tracking-widest">Part 2 of 2 · Outcome question</p>
          <h1 className="text-xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Come back in a few days
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            We saved Q4 — <b>"did the treatment work?"</b> — for after you've actually lived through the plan. It opens on{' '}
            <b>{unlockAt ? unlockAt.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : 'day 7'}</b>.
          </p>
          <p className="text-xs text-slate-500 mt-3">
            You'll get a free in-app notification on the day. No SMS. No spam. One question + an optional note — done in 20 seconds.
          </p>
        </div>
      </div>
    );
  }

  // ── Outcome stage: Q4 + notes ─────────────────────────────────────────
  if (samedayDone && !outcomeDone && outcomeUnlocked) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8 shadow-sm" data-testid="tqs-outcome-stage">
            <div className="border-b border-slate-100 pb-5 mb-6">
              <p className="text-xs font-bold text-sky-600 uppercase tracking-widest">Part 2 of 2 · Outcome rating</p>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                How did the treatment plan from <span className="text-sky-600">{meta?.doctor_name}</span> actually work?
              </h1>
              <p className="text-xs text-slate-500 mt-2">One honest yes/no question — submitted in the day-6–10 window so it counts at full weight.</p>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4 md:p-5" data-testid={`tqs-q-${OUTCOME_QUESTION.key}`}>
              <div className="flex items-start gap-3 mb-3">
                <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center shrink-0">4</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{OUTCOME_QUESTION.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{OUTCOME_QUESTION.sub}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setQ4(true)}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${q4 === true ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                  data-testid="tqs-q4-yes">
                  <CheckCircle size={16} /> Yes
                </button>
                <button onClick={() => setQ4(false)}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${q4 === false ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
                  data-testid="tqs-q4-no">
                  <XCircle size={16} /> No
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4 md:p-5 mt-4">
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Notes for the doctor <span className="text-xs text-slate-400 font-normal">(optional, private)</span></p>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={500}
                placeholder="A line about what worked or what could improve…"
                className="w-full mt-2 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300 resize-none"
                data-testid="tqs-note" />
            </div>

            {err && <p className="mt-4 text-sm text-rose-600">{err}</p>}
            <button onClick={submitOutcome} disabled={!q4Answered || submitting}
              className="w-full mt-6 py-3.5 rounded-full bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="tqs-submit-outcome">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              {q4Answered ? 'Submit outcome' : 'Tap Yes or No to submit'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Default: sameday stage (Q1-Q3) ────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-5xl mx-auto grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8 shadow-sm" data-testid="tqs-survey">
            <div className="border-b border-slate-100 pb-5 mb-6">
              <p className="text-xs font-bold text-sky-600 uppercase tracking-widest">Part 1 of 2 · DocSite Treatment Quality Score</p>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                How was your visit with <span className="text-sky-600">{meta?.doctor_name}</span>?
              </h1>
              <p className="text-xs text-slate-500 mt-2">3 quick yes/no questions about the visit. Q4 about the outcome opens after day 6 (we'll notify you in-app — no SMS spam).</p>
            </div>

            {meta?.appointment_at && (
              <div className="lg:hidden">
                <FreshnessCountdown appointmentAt={meta.appointment_at} />
              </div>
            )}

            <div className="space-y-5">
              {SAMEDAY_QUESTIONS.map((q, i) => (
                <div key={q.key} className="rounded-2xl border border-slate-200 p-4 md:p-5" data-testid={`tqs-q-${q.key}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center shrink-0">{i+1}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{q.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{q.sub}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setAnswers(a => ({ ...a, [q.key]: true }))}
                      className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${answers[q.key] === true ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                      data-testid={`tqs-${q.key}-yes`}>
                      <CheckCircle size={16} /> Yes
                    </button>
                    <button onClick={() => setAnswers(a => ({ ...a, [q.key]: false }))}
                      className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${answers[q.key] === false ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
                      data-testid={`tqs-${q.key}-no`}>
                      <XCircle size={16} /> No
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {err && <p className="mt-4 text-sm text-rose-600">{err}</p>}
            <button onClick={submitSameday} disabled={!samedayAnswered || submitting}
              className="w-full mt-6 py-3.5 rounded-full bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="tqs-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              {samedayAnswered ? 'Submit part 1 (Q1–Q3)' : `Answer all 3 questions (${Object.keys(answers).length}/3)`}
            </button>
            <p className="text-center text-[10px] text-slate-400 mt-3">Q4 about the outcome opens day 7 — we'll send you a free in-app notification.</p>
          </div>
        </div>

        {meta?.appointment_at && (
          <aside className="hidden lg:block lg:col-span-1" data-testid="tqs-countdown-rail">
            <div className="lg:sticky lg:top-8">
              <FreshnessCountdown appointmentAt={meta.appointment_at} />
              <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                The longer you wait, the less your honest answers count in DocSite's algorithm. Submit now to lock in the highest weighting.
              </p>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FreshnessCountdown — shows live decay multiplier for Q1-Q3.
// =============================================================================

const SAMEDAY_BANDS = [
  { upTo: 24, weight: 100, tone: 'emerald', toneBg: 'from-emerald-50 to-emerald-100', toneBorder: 'border-emerald-300', toneText: 'text-emerald-700', toneAccent: 'bg-emerald-500' },
  { upTo: 48, weight: 70,  tone: 'amber',   toneBg: 'from-amber-50 to-amber-100',     toneBorder: 'border-amber-300',   toneText: 'text-amber-700',   toneAccent: 'bg-amber-500' },
  { upTo: 72, weight: 40,  tone: 'orange',  toneBg: 'from-orange-50 to-orange-100',   toneBorder: 'border-orange-300',  toneText: 'text-orange-700',  toneAccent: 'bg-orange-500' },
  { upTo: Infinity, weight: 20, tone: 'rose', toneBg: 'from-rose-50 to-rose-100',     toneBorder: 'border-rose-300',    toneText: 'text-rose-700',    toneAccent: 'bg-rose-500' },
];

function FreshnessCountdown({ appointmentAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const consultMs = useMemo(() => {
    try { return new Date(appointmentAt).getTime(); } catch { return null; }
  }, [appointmentAt]);

  if (!consultMs || isNaN(consultMs)) return null;

  const elapsedH = (now - consultMs) / 3_600_000;
  if (elapsedH < 0) return null;

  const bandIdx = SAMEDAY_BANDS.findIndex(b => elapsedH < b.upTo);
  const band = SAMEDAY_BANDS[bandIdx === -1 ? SAMEDAY_BANDS.length - 1 : bandIdx];
  const next = SAMEDAY_BANDS[bandIdx + 1] || null;
  const hoursToNextDrop = next ? Math.max(0, next.upTo - elapsedH) : null;
  const consultedAgo = formatRel(elapsedH);

  return (
    <div className={`rounded-2xl border-2 ${band.toneBorder} bg-gradient-to-br ${band.toneBg} p-5 mb-6`} data-testid="tqs-freshness-countdown">
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-full ${band.toneAccent} text-white flex items-center justify-center shrink-0 shadow-md`}>
          <Clock size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${band.toneText}`}>Freshness multiplier · live</p>
          <div className="flex items-baseline gap-3 mt-0.5 flex-wrap">
            <span className="text-4xl md:text-5xl font-bold tabular-nums text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="tqs-current-weight">
              {band.weight}%
            </span>
            <span className="text-sm text-slate-700">
              your rating is currently worth <strong>{band.weight}%</strong> in DocSite's algorithm.
            </span>
          </div>

          {next ? (
            next.upTo === Infinity ? (
              <p className={`text-sm mt-2 ${band.toneText} font-semibold`} data-testid="tqs-countdown-text">
                After 72h since the visit it drops to <strong>20%</strong> minimum — submit now to lock in {band.weight}%.
              </p>
            ) : (
              <p className={`text-sm mt-2 ${band.toneText} font-semibold`} data-testid="tqs-countdown-text">
                Drops to <strong>{next.weight}%</strong> in <Countdown hours={hoursToNextDrop} /> — submit before then to lock in {band.weight}%.
              </p>
            )
          ) : (
            <p className="text-sm mt-2 text-rose-700 font-semibold flex items-center gap-1.5" data-testid="tqs-countdown-text">
              <AlertTriangle size={14} /> Submission counted at minimum 20% — but your honest answers still help.
            </p>
          )}

          <div className="grid grid-cols-4 gap-1.5 mt-4">
            {SAMEDAY_BANDS.map((b, i) => {
              const active = b.weight === band.weight;
              return (
                <div key={i} className={`text-center rounded-lg px-1.5 py-1.5 border ${active ? `${b.toneBorder} bg-white` : 'border-slate-200 bg-white/50 opacity-50'}`}>
                  <p className={`text-[10px] font-bold tabular-nums ${active ? b.toneText : 'text-slate-500'}`}>{b.weight}%</p>
                  <p className="text-[9px] text-slate-500 leading-tight mt-0.5">
                    {i === 0 ? 'Same day' : i === 1 ? '24-48h' : i === 2 ? '48-72h' : '72h+'}
                  </p>
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-slate-500 mt-3">
            Consulted {consultedAgo}. Q1-Q3 measure what happened in the consult — memory fades fast, so we weight same-day answers fullest.
          </p>
        </div>
      </div>
    </div>
  );
}

function Countdown({ hours }) {
  if (hours == null) return <span>—</span>;
  const totalMin = Math.max(0, Math.floor(hours * 60));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return <span className="tabular-nums">{h > 0 ? `${h}h ` : ''}{m}m</span>;
}

function formatRel(elapsedH) {
  if (elapsedH < 1) {
    const m = Math.floor(elapsedH * 60);
    return m <= 1 ? 'just now' : `${m} minutes ago`;
  }
  if (elapsedH < 24) {
    const h = Math.floor(elapsedH);
    return h === 1 ? '1 hour ago' : `${h} hours ago`;
  }
  const d = Math.floor(elapsedH / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}
